/**
 * Pricing helpers for search/listing display.
 */

type FreshnessState = 'fresh' | 'aging' | 'stale' | 'unavailable';

/** How many days past its last check a listing's price stays "valid" (#254). */
const FRESHNESS_VALIDITY_DAYS: Record<FreshnessState, number> = {
  fresh: 2,
  aging: 1,
  stale: 0,
  unavailable: 0,
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

interface PriceValidityInput {
  lastVerifiedAt?: string | null;
  lastScrapedAt?: string | null;
  dataFreshness?: FreshnessState;
}

/**
 * Derive a schema.org `Offer.priceValidUntil` date (YYYY-MM-DD) from a
 * listing's provenance (#254).
 *
 * The base is the most authoritative check time — `lastVerifiedAt`, falling
 * back to `lastScrapedAt` — extended by a freshness-dependent window so the
 * machine-readable validity matches how long we actually vouch for the price:
 * fresh listings get 2 days, aging 1, and stale/unavailable get 0 (the price
 * is honestly already past its guarantee). Returns `undefined` when there's no
 * usable timestamp, so the caller omits the field rather than emitting a guess.
 *
 * Deterministic: derived only from the listing's own timestamps, never the
 * wall clock.
 *
 * @param input listing provenance fields
 * @returns ISO date string (YYYY-MM-DD) or undefined
 */
export function offerPriceValidUntil(input: PriceValidityInput): string | undefined {
  const baseRaw = input.lastVerifiedAt ?? input.lastScrapedAt ?? null;
  if (!baseRaw) return undefined;
  const t = Date.parse(baseRaw);
  if (!Number.isFinite(t)) return undefined;
  const days = input.dataFreshness ? FRESHNESS_VALIDITY_DAYS[input.dataFreshness] : 1;
  return new Date(t + days * MS_PER_DAY).toISOString().slice(0, 10);
}

interface CprInput {
  pricePerRound: number | null;
  price: number | null;
  roundCount: number | null;
}

/**
 * Effective price-per-round for a listing (#114).
 *
 * Prefer the indexed `pricePerRound`, but when it's missing yet the total
 * `price` and `roundCount` are both present, derive it (price ÷ rounds) so the
 * UI shows a number instead of a dash for what is simple arithmetic. Returns
 * `null` only when CPR genuinely can't be determined.
 *
 * @param {CprInput} hit
 * @returns {number | null}
 */
export function effectiveCpr(hit: CprInput): number | null {
  if (hit.pricePerRound != null && Number.isFinite(hit.pricePerRound)) {
    return hit.pricePerRound;
  }
  if (
    hit.price != null &&
    Number.isFinite(hit.price) &&
    hit.roundCount != null &&
    hit.roundCount > 0
  ) {
    return hit.price / hit.roundCount;
  }
  return null;
}

interface RoundCountInput {
  roundCount: number | null;
  // Sequelize DECIMAL columns arrive as strings, so accept either.
  price: number | string | null;
  pricePerRound: number | string | null;
}

/** Coerce a number-or-decimal-string to a finite number, or null. */
function toFiniteNumber(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'string' ? Number(value) : value;
  return Number.isFinite(n) ? n : null;
}

/**
 * Effective round count for a listing (#116).
 *
 * Prefer the stored `roundCount`, but when it's missing yet `price` and
 * `pricePerRound` are both present, recover it (price ÷ pricePerRound, rounded)
 * — the exact inverse of {@link effectiveCpr}, since pricePerRound is itself
 * price ÷ rounds. Returns `null` when it can't be determined.
 *
 * @param {RoundCountInput} listing
 * @returns {number | null}
 */
export function effectiveRoundCount(listing: RoundCountInput): number | null {
  if (listing.roundCount != null && Number.isFinite(listing.roundCount)) {
    return listing.roundCount;
  }
  const price = toFiniteNumber(listing.price);
  const cpr = toFiniteNumber(listing.pricePerRound);
  if (price != null && cpr != null && cpr > 0) {
    return Math.round(price / cpr);
  }
  return null;
}
